import { useApi } from '@/shared/api/http';
import type { RequestParams, StatusResponse } from '@/shared/model/types/http';
import type {
  IMarkAsViewedData,
  IMarkAllAsViewedData,
  IModerationInquiryData,
  ISendOfferData,
  IPatchOfferData,
  IDismissWorkerData,
  IInquiryOffer,
  IInquiryRecord,
} from '@/entities/lawyer/model/types/inquiry';
import type { ILawyerSearchRequest } from '@/entities/lawyer/model/types/lawyerSearch';
import type { IAppointmentSettingsData } from '@/entities/lawyer/model/types/appointmentSettings';
import type {
  ILawyerQuestionnairePayload,
  ICompanyQuestionnairePayload,
} from '@/entities/lawyer/model/types/questionnaire';
import type { ILawyerCardInfoRaw } from '@/entities/lawyer/model/types/lawyer';
import type { ILegalServiceInfo, IModeration } from '@/entities/user/@x/lawyer';
import type { ICallbackRow } from '@/entities/callback/@x/lawyer';

export const useLawyerService = () => {
  const api = useApi();

  const getServices = async () => {
    return await api.get<ILegalServiceInfo[]>('services/');
  };

  const getLawyers = async <T = ILawyerCardInfoRaw[], M = Record<string, unknown>>(
    dataReq: ILawyerSearchRequest,
    params: RequestParams,
  ) => {
    return await api.post<T, M>('lawyer/search', dataReq, {
      params,
    });
  };

  const getLawyerBySlug = async (slug: string) => {
    return await api.get(`lawyer/by-slug/${slug}/`);
  };

  const getLawyersByCoords = async (params: RequestParams) => {
    return await api.get<ILawyerCardInfoRaw[]>('lawyer/', {
      params,
    });
  };

  const getLawyersByTopicId = async <T>(params: RequestParams) => {
    return await api.get<T>('lawyer/', {
      params,
    });
  };

  const getCompanyWorkers = async (params: RequestParams) => {
    return await api.get('lawyer/company/workers/', {
      params,
    });
  };

  const getSelfCallbacks = async (lawyerSlug: string, params: RequestParams) => {
    return await api.get<ICallbackRow[]>(`lawyer/${lawyerSlug}/callback/`, {
      params,
    });
  };

  const deleteCompanyWorker = async (dataReq: IDismissWorkerData, offerId: number) => {
    return await api.post(`lawyer/company/update-worker/${offerId}`, dataReq);
  };

  const updateLawyerInfo = async (dataReq: ILawyerQuestionnairePayload) => {
    return await api.patch<IModeration>('questionnaire/lawyer', dataReq);
  };

  const updateOrganizationInfo = async (dataReq: ICompanyQuestionnairePayload) => {
    return await api.patch<IModeration>('questionnaire/company', dataReq);
  };

  const updateAppointmentSettings = async (dataReq: IAppointmentSettingsData) => {
    return await api.post<StatusResponse>('lawyer/appointment', dataReq);
  };

  const sendDataToModeration = async (dataReq: IModerationInquiryData) => {
    return await api.post<IInquiryRecord>('inquiry', dataReq);
  };

  const sendOfferToLawyer = async <T = IInquiryOffer>(dataReq: ISendOfferData) => {
    return await api.post<T>('inquiry', dataReq);
  };

  const patchOffer = async (dataReq: IPatchOfferData, offerId: number) => {
    return await api.patch<IInquiryRecord>(`inquiry/${offerId}`, dataReq);
  };

  const getOffersList = async (params: RequestParams) => {
    return await api.get('inquiry/', {
      params,
    });
  };

  const markAsViewed = async (dataReq: IMarkAsViewedData) => {
    return await api.post<StatusResponse>('inquiry/viewed', dataReq);
  };

  const markAllAsViewed = async (dataReq?: IMarkAllAsViewedData) => {
    return await api.post<StatusResponse>('inquiry/viewed-all', dataReq);
  };

  return {
    getServices,
    getLawyers,
    getLawyerBySlug,
    getLawyersByCoords,
    getLawyersByTopicId,
    getCompanyWorkers,
    getSelfCallbacks,
    deleteCompanyWorker,
    updateLawyerInfo,
    updateOrganizationInfo,
    updateAppointmentSettings,
    sendDataToModeration,
    sendOfferToLawyer,
    patchOffer,
    getOffersList,
    markAsViewed,
    markAllAsViewed,
  };
};
